import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { MultiplayerGameState, createPlayerState } from '../multiplayer/MultiplayerGameState';
import { AIOpponent, AI_DIFFICULTY } from '../multiplayer/AIOpponent';
import { getMultiplayerService } from '../multiplayer/OnlineMultiplayerService';
import { checkPrerequisites } from '../multiplayer/CardRulesEngine';
import { cardStyle, getRandomAIDeck } from '../CardData';
import TutorialOverlay, { shouldShowTutorial } from './Tutorial';
import './Game.css';
import '../styles/accessibility.css';

// Responsive sizing hook
const useResponsiveLayout = () => {
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth < 480,
    isTablet: window.innerWidth >= 480 && window.innerWidth < 992,
    isDesktop: window.innerWidth >= 992,
    isLandscape: window.innerWidth > window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
        isMobile: window.innerWidth < 480,
        isTablet: window.innerWidth >= 480 && window.innerWidth < 992,
        isDesktop: window.innerWidth >= 992,
        isLandscape: window.innerWidth > window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return screenSize;
};

const MultiplayerGame = ({ gameConfig, onExit }) => {
  // Tutorial state
  const [showTutorial, setShowTutorial] = useState(() => shouldShowTutorial('versus'));
  
  // Accessibility: Screen reader announcements
  const [screenReaderAnnouncement, setScreenReaderAnnouncement] = useState('');
  const announceToScreenReader = (message) => {
    setScreenReaderAnnouncement('');
    setTimeout(() => setScreenReaderAnnouncement(message), 100);
  };
  
  const [gameState, setGameState] = useState(null);
  const [localPlayerId, setLocalPlayerId] = useState(null);
  const [aiOpponent, setAiOpponent] = useState(null);
  const [selectedOpponentDiscard, setSelectedOpponentDiscard] = useState(null);
  const [showOpponentDiscardModal, setShowOpponentDiscardModal] = useState(false);
  const [showGameEndModal, setShowGameEndModal] = useState(false);
  const [aiThinking, setAiThinking] = useState(false);
  const [selectedCardFromOpponent, setSelectedCardFromOpponent] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [draggedCard, setDraggedCard] = useState(null);
  const [draggedCardIndex, setDraggedCardIndex] = useState(null);
  const [isDraggingToPlay, setIsDraggingToPlay] = useState(false);
  const [isDraggingToDiscard, setIsDraggingToDiscard] = useState(false);
  
  // Touch drag state for mobile
  const [touchDragPosition, setTouchDragPosition] = useState(null);
  const [pressStartInfo, setPressStartInfo] = useState(null);
  const [touchDragInitiated, setTouchDragInitiated] = useState(false);
  const playAreaRef = useRef(null);
  const discardAreaRef = useRef(null);
  
  // Expanded card view state (like solitaire)
  const [expandedCard, setExpandedCard] = useState(null);
  const [expandedCardIndex, setExpandedCardIndex] = useState(null);
  const [expandedCardIsInHand, setExpandedCardIsInHand] = useState(false);
  const [isClosingExpanded, setIsClosingExpanded] = useState(false);
  const [expandedCardPosition, setExpandedCardPosition] = useState(null);
  const [expandedCardOriginalPosition, setExpandedCardOriginalPosition] = useState(null);
  const [hoveredCardIndex, setHoveredCardIndex] = useState(null);
  
  const multiplayerService = getMultiplayerService();
  const layout = useResponsiveLayout();
  
  const handSize = gameState?.getPlayerById(localPlayerId)?.hand?.length || 5;
  const cardWidth = 140;
  const minSpacing = 35;
  const maxSpacing = 180;
  const containerPadding = 80;
  const availableWidth = layout.width - containerPadding;
  const neededWidthForAllCards = handSize * cardWidth;
  
  let cardSpacing;
  if (neededWidthForAllCards <= availableWidth) {
    cardSpacing = Math.min(maxSpacing, Math.max(minSpacing, (availableWidth - cardWidth) / Math.max(handSize - 1, 1)));
  } else {
    cardSpacing = Math.max(minSpacing, (availableWidth - cardWidth) / Math.max(handSize - 1, 1));
  }
  
  // Responsive card sizes based on screen
  const cardSizes = useMemo(() => {
    if (layout.isMobile) {
      return {
        hand: { width: '100px', imageHeight: '65px' },
        played: { width: '60px', imageHeight: '40px' },
        discard: { width: '70px', imageHeight: '48px' },
      };
    } else if (layout.isTablet) {
      return {
        hand: { width: '120px', imageHeight: '80px' },
        played: { width: '80px', imageHeight: '55px' },
        discard: { width: '90px', imageHeight: '60px' },
      };
    } else {
      return {
        hand: { width: '140px', imageHeight: '100px' },
        played: { width: '100px', imageHeight: '70px' },
        discard: { width: '100px', imageHeight: '70px' },
      };
    }
  }, [layout.isMobile, layout.isTablet]);

  // Initialize game
  useEffect(() => {
    initializeGame();

    // Set up online multiplayer listeners if in online mode
    if (gameConfig.mode === 'online') {
      multiplayerService.on('game_action', handleRemoteAction);
      multiplayerService.on('game_state_update', handleGameStateUpdate);

      return () => {
        multiplayerService.off('game_action', handleRemoteAction);
        multiplayerService.off('game_state_update', handleGameStateUpdate);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // AI turn logic
  useEffect(() => {
    if (!gameState || gameState.gameEnded) return;

    const currentPlayer = gameState.getCurrentPlayer();
    if (currentPlayer && currentPlayer.isAI && gameConfig.mode === 'ai') {
      // AI's turn - execute after a delay
      const timeout = setTimeout(() => {
        executeAITurn();
      }, 1000);

      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState?.currentPlayerIndex, gameState?.turnNumber]);

  const initializeGame = () => {
    // Get AI difficulty for deck selection
    const aiDifficulty = gameConfig.aiDifficulty || 'medium';
    
    // Create players with appropriate decks
    const players = gameConfig.players.map(p => {
      if (p.isAI) {
        // Get a random deck appropriate for AI difficulty
        const aiDeckConfig = getRandomAIDeck(aiDifficulty);
        console.log(`AI "${p.name}" using deck: ${aiDeckConfig.name} (${aiDeckConfig.description})`);
        return createPlayerState(p.id, p.name, p.isAI, aiDeckConfig.cards);
      }
      // Human player uses their own deck
      return createPlayerState(p.id, p.name, p.isAI);
    });

    // Create game state
    const newGameState = new MultiplayerGameState(players, gameConfig.mode);
    newGameState.initializeGame();

    setGameState(newGameState);

    // Set local player ID (first non-AI player)
    const localPlayer = players.find(p => !p.isAI);
    if (localPlayer) {
      setLocalPlayerId(localPlayer.id);
    }

    // Initialize AI if needed
    if (gameConfig.mode === 'ai') {
      const aiPlayer = players.find(p => p.isAI);
      if (aiPlayer) {
        setAiOpponent(new AIOpponent(AI_DIFFICULTY[aiDifficulty.toUpperCase()]));
      }
    }
  };

  const executeAITurn = async () => {
    if (!gameState || !aiOpponent) return;

    const currentPlayer = gameState.getCurrentPlayer();
    
    // Don't execute if AI player is already finished
    if (currentPlayer.isFinished) {
      return;
    }

    setAiThinking(true);

    const decision = await aiOpponent.makeDecision(gameState, currentPlayer.id);

    if (decision) {
      // Execute the decision
      const newGameState = gameState.clone();

      if (decision.type === 'play_from_hand') {
        newGameState.playCardFromHand(currentPlayer.id, decision.cardIndex, 'played');
      } else if (decision.type === 'discard') {
        newGameState.discardCard(currentPlayer.id, decision.cardIndex);
      } else if (decision.type === 'play_from_opponent') {
        newGameState.playCardFromOpponentDiscard(
          currentPlayer.id,
          decision.opponentId,
          decision.cardIndex
        );
      }

      // Calculate and update energy
      const energy = calculatePlayerEnergy(newGameState, currentPlayer.id);
      newGameState.updatePlayerEnergy(currentPlayer.id, energy);

      // End turn
      newGameState.endTurn();
      setGameState(newGameState);
      
      // Announce AI action to screen readers
      const actionDescription = decision.type === 'play_from_hand' 
        ? `AI played ${decision.card.name}` 
        : decision.type === 'discard' 
          ? `AI discarded ${decision.card.name}`
          : `AI played ${decision.card.name} from your discard pile`;
      announceToScreenReader(`${actionDescription}. It is now your turn.`);
    }

    setAiThinking(false);
  };

  const calculatePlayerEnergy = (state, playerId) => {
    const player = state.getPlayerById(playerId);
    if (!player) return 0;

    // Energy from player's own played cards
    const ownCardsEnergy = player.playedCards.reduce((total, card) => total + (card.energy || 0), 0);
    
    // Energy from center cards that THIS player played (stolen from opponent's discard)
    const centerCardsEnergy = state.getCenterCardsByPlayer(playerId)
      .reduce((total, card) => total + (card.energy || 0), 0);
    
    return ownCardsEnergy + centerCardsEnergy;
  };

  const handleRemoteAction = (data) => {
    console.log('Remote action:', data);
  };

  const handleGameStateUpdate = (data) => {
    const updatedState = MultiplayerGameState.deserialize(data.gameState);
    setGameState(updatedState);
  };

  const handlePlayFromHand = (cardIndex) => {
    if (!gameState || gameState.gameEnded) return;

    const currentPlayer = gameState.getCurrentPlayer();
    if (currentPlayer.id !== localPlayerId) {
      return;
    }
    
    // Can't play if finished
    if (currentPlayer.isFinished) {
      return;
    }

    if (currentPlayer.usedSpaces >= currentPlayer.spaces) {
      alert("No more spaces available!");
      return;
    }

    // Get the card and check prerequisites (include center cards)
    const card = currentPlayer.hand[cardIndex];
    const centerCards = gameState.getCenterCards();
    if (!checkPrerequisites(card, currentPlayer.playedCards, centerCards)) {
      alert(`Cannot play ${card.name}: Prerequisites not met!\n\nCheck the card description for requirements.`);
      return;
    }

    const newGameState = gameState.clone();
    const playedCard = newGameState.playCardFromHand(localPlayerId, cardIndex, 'played');

    if (playedCard) {
      // Calculate energy
      const energy = calculatePlayerEnergy(newGameState, localPlayerId);
      newGameState.updatePlayerEnergy(localPlayerId, energy);

      // End turn
      newGameState.endTurn();
      setGameState(newGameState);
      
      // Announce to screen readers
      announceToScreenReader(`You played ${playedCard.name}. Energy: ${energy}. ${opponent?.name || 'AI'}'s turn.`);

      // Send action to server if online
      if (gameConfig.mode === 'online') {
        multiplayerService.sendAction({
          type: 'play_from_hand',
          cardIndex,
          playerId: localPlayerId
        });
      }
    }
  };

  const handleDiscard = (cardIndex) => {
    if (!gameState || gameState.gameEnded) return;

    const currentPlayer = gameState.getCurrentPlayer();
    if (currentPlayer.id !== localPlayerId) {
      return;
    }
    
    // Can't discard if finished
    if (currentPlayer.isFinished) {
      return;
    }

    const newGameState = gameState.clone();
    const discardedCard = currentPlayer.hand[cardIndex];
    newGameState.discardCard(localPlayerId, cardIndex);

    // End turn
    newGameState.endTurn();
    setGameState(newGameState);
    
    // Announce to screen readers
    announceToScreenReader(`You discarded ${discardedCard?.name || 'a card'}. ${opponent?.name || 'AI'}'s turn.`);

    if (gameConfig.mode === 'online') {
      multiplayerService.sendAction({
        type: 'discard',
        cardIndex,
        playerId: localPlayerId
      });
    }
  };

  const handlePlayFromOpponentDiscard = (opponentId = null, cardIdx = null) => {
    if (!gameState) return;

    // Use provided values or fall back to modal selections
    const targetOpponentId = opponentId || selectedOpponentDiscard?.opponentId;
    const targetCardIndex = cardIdx !== null ? cardIdx : 
                           (selectedCardFromOpponent !== null ? selectedCardFromOpponent : 0);
    
    if (!targetOpponentId) return;

    const currentPlayer = gameState.getCurrentPlayer();
    if (currentPlayer.id !== localPlayerId) {
      return;
    }

    if (currentPlayer.usedSpaces >= currentPlayer.spaces) {
      alert("No more spaces available!");
      return;
    }

    // Get fresh opponent data from current game state
    const opponent = gameState.getPlayerById(targetOpponentId);
    if (!opponent || opponent.discardPile.length === 0) {
      alert("No cards available in opponent's discard pile!");
      return;
    }

    if (targetCardIndex < 0 || targetCardIndex >= opponent.discardPile.length) {
      alert("Invalid card selection!");
      return;
    }

    // Check if the card can be shared (Wisdom cards cannot be shared except Love)
    const cardToPlay = opponent.discardPile[targetCardIndex];
    if (cardToPlay.type === 'Wisdom' && cardToPlay.name !== 'Love') {
      alert(`Cannot play ${cardToPlay.name}: Wisdom cards cannot be played from opponent's discard (except Love).`);
      return;
    }
    
    // Check prerequisites before allowing to play card from opponent's discard
    const centerCards = gameState.getCenterCards();
    if (!checkPrerequisites(cardToPlay, currentPlayer.playedCards, centerCards)) {
      alert(`Cannot play ${cardToPlay.name}: Prerequisites not met!\n\nYou must have the required cards in your played area or shared cards.`);
      return;
    }

    const newGameState = gameState.clone();
    const card = newGameState.playCardFromOpponentDiscard(
      localPlayerId,
      targetOpponentId,
      targetCardIndex
    );

    if (card) {
      // Calculate energy for ONLY the local player
      const energy = calculatePlayerEnergy(newGameState, localPlayerId);
      newGameState.updatePlayerEnergy(localPlayerId, energy);

      // End turn
      newGameState.endTurn();
      setGameState(newGameState);
      setShowOpponentDiscardModal(false);
      setSelectedOpponentDiscard(null);
      setSelectedCardFromOpponent(null);

      if (gameConfig.mode === 'online') {
        multiplayerService.sendAction({
          type: 'play_from_opponent',
          opponentId: targetOpponentId,
          cardIndex: targetCardIndex,
          playerId: localPlayerId
        });
      }
    }
  };

  const openOpponentDiscardModal = (opponent) => {
    if (opponent.discardPile.length === 0) {
      alert("Opponent's discard pile is empty!");
      return;
    }

    setSelectedOpponentDiscard({ opponentId: opponent.id, cardIndex: 0, opponent });
    setShowOpponentDiscardModal(true);
  };

  // Check if a card can be played from opponent's discard (shared)
  // Wisdom cards cannot be shared except for Love
  const canCardBeShared = (card) => {
    if (card.type === 'Wisdom' && card.name !== 'Love') {
      return false;
    }
    return true;
  };

  // Open expanded card view (like solitaire) with position tracking for animation
  const openExpandedCard = (card, index, isInHand = false, cardElement = null) => {
    if (cardElement) {
      const rect = cardElement.getBoundingClientRect();
      const position = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
      setExpandedCardPosition(position);
      setExpandedCardOriginalPosition(position);
    }
    setExpandedCard(card);
    setExpandedCardIndex(index);
    setExpandedCardIsInHand(isInHand);
    setIsClosingExpanded(false);
    // Trigger animation to center
    setTimeout(() => setExpandedCardPosition(null), 10);
  };

  // Close expanded card with animation back to original position
  const closeExpandedCard = () => {
    setIsClosingExpanded(true);
    setExpandedCardPosition(expandedCardOriginalPosition);
    setTimeout(() => {
      setExpandedCard(null);
      setExpandedCardIndex(null);
      setExpandedCardIsInHand(false);
      setIsClosingExpanded(false);
      setExpandedCardPosition(null);
      setExpandedCardOriginalPosition(null);
    }, 400);
  };

  // Check if game ended
  useEffect(() => {
    if (gameState && gameState.gameEnded && !showGameEndModal) {
      setShowGameEndModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState?.gameEnded]);

  // Drag handlers (HTML5 + Touch) - matching Solitaire mode
  const handleDragStart = (e, card, index) => {
    setDraggedCard(card);
    setDraggedCardIndex(index);
    setTouchDragInitiated(true);
    // Set initial drag position
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setTouchDragPosition({ x: clientX, y: clientY });
    
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', JSON.stringify({ cardIndex: index }));
      // Create a transparent drag image
      const dragImage = new Image();
      dragImage.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
      e.dataTransfer.setDragImage(dragImage, 0, 0);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (draggedCard) {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      setTouchDragPosition({ x: clientX, y: clientY });
      
      // Check drop zones
      if (playAreaRef.current) {
        const playRect = playAreaRef.current.getBoundingClientRect();
        const overPlay = clientX >= playRect.left && clientX <= playRect.right &&
                        clientY >= playRect.top && clientY <= playRect.bottom;
        setIsDraggingToPlay(overPlay);
      }
      if (discardAreaRef.current) {
        const discardRect = discardAreaRef.current.getBoundingClientRect();
        const overDiscard = clientX >= discardRect.left && clientX <= discardRect.right &&
                          clientY >= discardRect.top && clientY <= discardRect.bottom;
        setIsDraggingToDiscard(overDiscard);
      }
    }
  };

  const handleDragEnd = () => {
    setDraggedCard(null);
    setDraggedCardIndex(null);
    setIsDraggingToPlay(false);
    setIsDraggingToDiscard(false);
    setTouchDragPosition(null);
    setTouchDragInitiated(false);
    setPressStartInfo(null);
    setHoveredCardIndex(null);
  };

  const handleDropOnPlayArea = () => {
    if (draggedCardIndex !== null) {
      handlePlayFromHand(draggedCardIndex);
    }
    handleDragEnd();
  };

  const handleDropOnDiscardArea = () => {
    if (draggedCardIndex !== null) {
      handleDiscard(draggedCardIndex);
    }
    handleDragEnd();
  };
  
  // Global touch move handler for dragging
  useEffect(() => {
    const handleGlobalTouchMove = (e) => {
      if (touchDragInitiated && draggedCard) {
        e.preventDefault();
        const touch = e.touches[0];
        setTouchDragPosition({ x: touch.clientX, y: touch.clientY });
        
        // Check drop zones during drag
        if (playAreaRef.current) {
          const playRect = playAreaRef.current.getBoundingClientRect();
          const overPlay = touch.clientX >= playRect.left && touch.clientX <= playRect.right &&
                          touch.clientY >= playRect.top && touch.clientY <= playRect.bottom;
          setIsDraggingToPlay(overPlay);
        }
        if (discardAreaRef.current) {
          const discardRect = discardAreaRef.current.getBoundingClientRect();
          const overDiscard = touch.clientX >= discardRect.left && touch.clientX <= discardRect.right &&
                            touch.clientY >= discardRect.top && touch.clientY <= discardRect.bottom;
          setIsDraggingToDiscard(overDiscard);
        }
      }
    };
    
    const handleGlobalTouchEnd = (e) => {
      if (touchDragInitiated && draggedCardIndex !== null) {
        const touch = e.changedTouches[0];
        
        // Check if dropped on play area
        if (playAreaRef.current) {
          const playRect = playAreaRef.current.getBoundingClientRect();
          const overPlay = touch.clientX >= playRect.left && touch.clientX <= playRect.right &&
                          touch.clientY >= playRect.top && touch.clientY <= playRect.bottom;
          if (overPlay) {
            handlePlayFromHand(draggedCardIndex);
            handleDragEnd();
            return;
          }
        }
        
        // Check if dropped on discard area
        if (discardAreaRef.current) {
          const discardRect = discardAreaRef.current.getBoundingClientRect();
          const overDiscard = touch.clientX >= discardRect.left && touch.clientX <= discardRect.right &&
                            touch.clientY >= discardRect.top && touch.clientY <= discardRect.bottom;
          if (overDiscard) {
            handleDiscard(draggedCardIndex);
            handleDragEnd();
            return;
          }
        }
        
        handleDragEnd();
      }
    };

    if (touchDragInitiated) {
      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      document.addEventListener('touchend', handleGlobalTouchEnd);
      return () => {
        document.removeEventListener('touchmove', handleGlobalTouchMove);
        document.removeEventListener('touchend', handleGlobalTouchEnd);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touchDragInitiated, draggedCard, draggedCardIndex]);

  // Render card with responsive sizing
  const renderCard = (card, index, isInHand, isOpponent = false, isDiscardCard = false, isCenterCard = false, useFannedPosition = false, handLength = 0) => {
    const isCurrentPlayerTurn = gameState && gameState.getCurrentPlayer()?.id === localPlayerId;
    const localPlayer = gameState?.getPlayerById(localPlayerId);
    const centerCards = gameState?.getCenterCards() || [];
    
    // Check if card can be played (if in hand and it's your turn)
    // Include center cards in prerequisite check
    const canPlay = isInHand && !isOpponent && isCurrentPlayerTurn && localPlayer && 
                    checkPrerequisites(card, localPlayer.playedCards, centerCards);
    
    // Determine card size based on context
    const size = isDiscardCard ? cardSizes.discard : (isInHand ? cardSizes.hand : cardSizes.played);
    const fontSize = layout.isMobile ? { name: '8px', desc: '6px', energy: '9px' } :
                     layout.isTablet ? { name: '9px', desc: '7px', energy: '10px' } :
                     { name: '10px', desc: '8px', energy: '11px' };
    
    // Fan effect calculations (like solitaire)
    const isHovered = hoveredCardIndex === index;
    const centerIndex = (handLength - 1) / 2;
    const distanceFromCenter = index - centerIndex;
    const rotationAngle = distanceFromCenter * 2; // Slight fan effect
    const normalizedDistance = Math.abs(distanceFromCenter) / Math.max(handLength / 2, 1);
    const verticalOffset = (1 - normalizedDistance) * 12; // Max 12px higher in middle
    
    // Fanned card style for hand - matching Solitaire dimensions
    const handCardWidth = 140;
    const handCardHeight = 200;
    
    // Is this card being dragged?
    const isDragging = touchDragInitiated && draggedCardIndex === index;
    const isDraggedButNotHovered = touchDragInitiated && draggedCardIndex !== null && draggedCardIndex !== index;
    
    const fannedHandStyle = useFannedPosition ? {
      position: 'absolute',
      left: `${index * cardSpacing}px`,
      bottom: '10px',
      width: `${handCardWidth}px`,
      height: `${handCardHeight}px`,
      transition: isDragging ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      transform: isDragging
        ? 'scale(1.1) rotate(-5deg)'
        : isHovered && !gameState?.gameEnded && !localPlayer?.isFinished
          ? `translateY(${-20 - verticalOffset}px) scale(1.08) rotate(0deg)`
          : `translateY(${-verticalOffset}px) scale(1) rotate(${rotationAngle}deg)`,
      transformOrigin: isDragging ? 'center' : 'center bottom',
      zIndex: isDragging ? 3000 : isHovered ? 2000 : index,
      cursor: isDragging ? 'grabbing' : canPlay ? 'grab' : 'not-allowed',
      opacity: isDragging ? 0 : isDraggedButNotHovered ? 0.7 : (expandedCardIndex === index && !isClosingExpanded) ? 0 : 1,
      filter: isHovered && !isDragging ? 'brightness(1.1)' : isDraggedButNotHovered ? 'brightness(0.8)' : 'brightness(1)',
      boxShadow: isDragging
        ? '0 20px 50px rgba(0, 0, 0, 0.5), 0 0 60px rgba(255, 255, 255, 0.4)'
        : isHovered
          ? (canPlay 
              ? '0 20px 60px rgba(0, 255, 0, 0.9), 0 0 80px rgba(0, 255, 0, 0.7)' 
              : '0 20px 60px rgba(255, 0, 0, 0.9), 0 0 80px rgba(255, 0, 0, 0.7)')
          : (canPlay 
              ? '0 5px 15px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 255, 0, 0.3)' 
              : '0 5px 15px rgba(0, 0, 0, 0.3), 0 0 20px rgba(255, 0, 0, 0.3)'),
      pointerEvents: 'auto',
    } : {};
    
    // For non-hand cards
    const normalStyle = !useFannedPosition ? {
      width: size.width,
      cursor: 'pointer',
      opacity: (isInHand && !canPlay && !isOpponent && isCurrentPlayerTurn) ? 0.6 : 
               (isInHand && !isCurrentPlayerTurn) ? 0.7 : 1,
      margin: layout.isMobile ? '2px' : '4px',
      display: 'inline-block',
      border: isCenterCard ? '2px solid #9b59b6' : 'none',
      boxShadow: isCenterCard ? '0 0 10px rgba(155, 89, 182, 0.5)' : '0 4px 14px rgba(0, 0, 0, 0.3)',
      transition: 'all 0.2s ease',
      position: 'relative',
      flexShrink: 0,
    } : {};
    
    // Accessibility description
    const cardDescription = `${card.name}. ${card.type} card. ${card.energy ? `${card.energy} energy.` : ''} ${card.description}`;
    const playableStatus = isInHand && !isOpponent ? (canPlay ? 'Playable' : 'Cannot play - missing prerequisites') : '';
    const cardLocation = isInHand ? 'in your hand' : isCenterCard ? 'in shared center area' : isDiscardCard ? 'in discard pile' : 'played';
    
    return (
      <div
        key={card.id || index}
        role="button"
        tabIndex={isInHand && !isOpponent ? 0 : -1}
        aria-label={`${card.name}. ${cardLocation}. ${playableStatus}`}
        aria-describedby={`card-desc-${card.id || index}`}
        draggable={isInHand && isCurrentPlayerTurn && !isOpponent && !localPlayer?.isFinished && canPlay}
        onDragStart={(e) => {
          if (isInHand && !localPlayer?.isFinished && canPlay) {
            handleDragStart(e, card, index);
          }
        }}
        onDragEnd={handleDragEnd}
        onMouseDown={(e) => {
          if (isInHand && !isOpponent) {
            setPressStartInfo({ x: e.clientX, y: e.clientY, time: Date.now(), index, card });
          }
        }}
        onMouseUp={(e) => {
          // Treat as click if no drag was initiated
          if (!touchDragInitiated && !draggedCard) {
            openExpandedCard(card, index, isInHand && !isOpponent, e.currentTarget);
          }
          if (isInHand) {
            setPressStartInfo(null);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openExpandedCard(card, index, isInHand && !isOpponent, e.currentTarget);
          }
        }}
        // Touch events for mobile drag - matching Solitaire
        onTouchStart={(e) => {
          if (isInHand && !isOpponent) {
            const touch = e.touches[0];
            setPressStartInfo({ x: touch.clientX, y: touch.clientY, time: Date.now(), index, card });
            setHoveredCardIndex(index);
          }
        }}
        onTouchMove={(e) => {
          if (!pressStartInfo || !isInHand || isOpponent) return;
          if (localPlayer?.isFinished || !isCurrentPlayerTurn) return;
          
          const touch = e.touches[0];
          const dx = Math.abs(touch.clientX - pressStartInfo.x);
          const dy = Math.abs(touch.clientY - pressStartInfo.y);
          const movedEnough = dx + dy > 10;
          
          if (movedEnough && !touchDragInitiated && canPlay) {
            // Start drag
            e.preventDefault();
            handleDragStart(e, pressStartInfo.card, pressStartInfo.index);
          }
        }}
        onTouchEnd={(e) => {
          // If no drag, treat as tap to open expanded view
          if (!touchDragInitiated && !draggedCard && pressStartInfo) {
            openExpandedCard(pressStartInfo.card, pressStartInfo.index, isInHand && !isOpponent, e.currentTarget);
          }
          if (!touchDragInitiated) {
            setPressStartInfo(null);
            setHoveredCardIndex(null);
          }
        }}
        onMouseEnter={() => useFannedPosition && !touchDragInitiated && setHoveredCardIndex(index)}
        onMouseLeave={() => useFannedPosition && !touchDragInitiated && setHoveredCardIndex(null)}
        style={{
          ...cardStyle,
          ...(useFannedPosition ? fannedHandStyle : normalStyle),
        }}
      >
        {/* Hidden description for screen readers */}
        <span id={`card-desc-${card.id || index}`} className="sr-only">
          {cardDescription}
        </span>
        <img
          src={card.imageUrl}
          alt=""
          aria-hidden="true"
          style={{
            width: '100%',
            height: useFannedPosition ? '65%' : size.imageHeight,
            objectFit: 'cover',
            borderRadius: layout.isMobile ? '6px 6px 0 0' : '10px 10px 0 0',
            pointerEvents: 'none',
          }}
        />
        <div style={{ 
          padding: layout.isMobile ? '3px' : '5px', 
          height: useFannedPosition ? '35%' : 'auto',
          background: 'white', 
          borderRadius: layout.isMobile ? '0 0 6px 6px' : '0 0 10px 10px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}>
          <div style={{ fontWeight: 'bold', fontSize: useFannedPosition ? '12px' : fontSize.name, marginBottom: '1px', color: '#000', lineHeight: 1.2 }}>
            {card.name}
          </div>
          {useFannedPosition && (
            <div style={{ fontSize: '10px', color: '#666', lineHeight: '1.2', overflow: 'hidden' }}>
              {card.description}
            </div>
          )}
          {!useFannedPosition && !layout.isMobile && (
            <div style={{ fontSize: fontSize.desc, color: '#666', marginBottom: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {card.description}
            </div>
          )}
        </div>
        {useFannedPosition && !canPlay && isCurrentPlayerTurn && !isOpponent && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 0, 0, 0.25)',
            borderRadius: layout.isMobile ? '6px' : '10px',
            pointerEvents: 'none'
          }} />
        )}
        {/* Show who played this center card */}
        {isCenterCard && card.playedBy && (
          <div style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            backgroundColor: card.playedBy === localPlayerId ? '#27ae60' : '#e74c3c',
            color: 'white',
            padding: layout.isMobile ? '2px 4px' : '3px 6px',
            borderRadius: '8px',
            fontSize: layout.isMobile ? '7px' : '9px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
          }}>
            {card.playedBy === localPlayerId ? 'YOU' : 'OPP'}
          </div>
        )}
      </div>
    );
  };

  // Render hidden card (for opponent's hand) - responsive
  // eslint-disable-next-line no-unused-vars
  const renderHiddenCard = (index) => {
    const hiddenSize = layout.isMobile ? { w: '40px', h: '60px', font: '20px' } :
                       layout.isTablet ? { w: '55px', h: '80px', font: '24px' } :
                       { w: '70px', h: '100px', font: '28px' };
    return (
      <div
        key={`hidden-${index}`}
        role="img"
        aria-label={`Opponent's hidden card ${index + 1}`}
        style={{
          ...cardStyle,
          width: hiddenSize.w,
          height: hiddenSize.h,
          margin: layout.isMobile ? '2px' : '4px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: layout.isMobile ? '6px' : '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: hiddenSize.font,
          boxShadow: '0 3px 12px rgba(0, 0, 0, 0.3)',
          flexShrink: 0,
        }}
      >
        <span aria-hidden="true">🂠</span>
      </div>
    );
  };

  if (!gameState) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'white' }}>Loading...</div>;
  }

  const currentPlayer = gameState.getCurrentPlayer();
  const isMyTurn = currentPlayer && currentPlayer.id === localPlayerId;
  const localPlayer = gameState.getPlayerById(localPlayerId);
  
  // Get opponents relative to LOCAL player, not current turn player
  // This ensures we always see the same opponent regardless of whose turn it is
  const opponents = gameState.players.filter(p => p.id !== localPlayerId);
  const opponent = opponents[0]; // Get first opponent

  // Responsive spacing values
  const spacing = {
    section: layout.isMobile ? '8px' : layout.isTablet ? '12px' : '16px',
    container: layout.isMobile ? '6px' : layout.isTablet ? '10px' : '15px',
    gap: layout.isMobile ? '6px' : layout.isTablet ? '8px' : '10px',
  };
  
  const fontSizes = {
    header: layout.isMobile ? '14px' : layout.isTablet ? '16px' : '18px',
    subHeader: layout.isMobile ? '12px' : layout.isTablet ? '14px' : '16px',
    label: layout.isMobile ? '9px' : layout.isTablet ? '10px' : '12px',
    icon: layout.isMobile ? '18px' : layout.isTablet ? '20px' : '24px',
  };

  return (
    <div 
      className="Game page-transition" 
      role="main"
      aria-label="Neolysium Versus Mode Game"
      style={{ 
        height: CSS.supports?.('height', '100dvh') ? '100dvh' : '100vh',
        maxHeight: CSS.supports?.('height', '100dvh') ? '100dvh' : '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        touchAction: 'none', // Prevent scroll during game
      }}
    >
      {/* Screen Reader Announcements - Live Region */}
      <div 
        role="status" 
        aria-live="polite" 
        aria-atomic="true"
        className="sr-only"
      >
        {screenReaderAnnouncement}
      </div>
      
      {/* Tutorial Overlay for first-time players */}
      {showTutorial && (
        <TutorialOverlay 
          mode="versus" 
          onComplete={() => setShowTutorial(false)}
          onSkip={() => setShowTutorial(false)}
        />
      )}
      
      <div className="blur" style={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: spacing.container,
        paddingTop: layout.isMobile ? '40px' : '50px',
        overflow: 'hidden',
      }}>
        
        {/* Opponent's Board (Top) - Compact */}
        {opponent && (
          <div style={{ 
            marginBottom: spacing.section,
            flexShrink: 0,
          }}>
            {/* Opponent Header - Compact */}
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: layout.isMobile ? '6px 10px' : '8px 12px',
              background: opponent.isFinished 
                ? 'linear-gradient(135deg, rgba(155, 89, 182, 0.3) 0%, rgba(155, 89, 182, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255, 100, 100, 0.2) 0%, rgba(255, 50, 50, 0.1) 100%)',
              borderRadius: layout.isMobile ? '6px' : '10px',
              marginBottom: spacing.gap,
              border: opponent.isFinished 
                ? '2px solid rgba(155, 89, 182, 0.5)' 
                : '1px solid rgba(255, 100, 100, 0.3)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: layout.isMobile ? '6px' : '10px' }}>
                <span style={{ fontSize: fontSizes.icon }}>{opponent.isAI ? 'AI' : 'P2'}</span>
                <strong className="appText" style={{ fontSize: fontSizes.header }}>
                  {layout.isMobile ? opponent.name.substring(0, 8) : opponent.name}
                  {opponent.isFinished && <span style={{ color: '#9b59b6', marginLeft: '6px' }}>(Finished)</span>}
                </strong>
              </div>
              <div style={{ 
                display: 'flex', 
                gap: layout.isMobile ? '8px' : '15px',
                alignItems: 'center'
              }}>
                <span className="appText" style={{ fontSize: fontSizes.subHeader, color: '#ffd700', display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="#ffd700" stroke="#ffd700" strokeWidth="1"/>
                  </svg>
                  {opponent.energy}
                </span>
                <span className="appText" style={{ fontSize: fontSizes.label, opacity: 0.8, display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <rect x="7" y="4" width="12" height="16" rx="1" fill="rgba(255, 255, 255, 0.6)" stroke="rgba(255, 255, 255, 0.8)" strokeWidth="1"/>
                  </svg>
                  {opponent.hand.length}
                </span>
              </div>
            </div>

            {/* Opponent's board row: Played Cards + Discard Pile */}
            <div style={{ display: 'flex', gap: spacing.gap }}>
            {/* Opponent Played Cards Area */}
            <div className="played-cards-area" style={{
                flex: 1,
                minHeight: layout.isMobile ? '80px' : layout.isTablet ? '100px' : '120px',
                maxHeight: layout.isMobile ? '100px' : layout.isTablet ? '130px' : '160px',
                backgroundColor: 'rgba(255, 100, 100, 0.1)',
                boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.10)',
                borderRadius: layout.isMobile ? '6px' : '10px',
                padding: layout.isMobile ? '5px' : '8px',
              display: 'flex',
              flexDirection: 'column',
                border: '1px solid rgba(255, 100, 100, 0.2)',
                overflow: 'auto',
              }}>
                <div style={{ 
                  fontSize: fontSizes.label, 
                  opacity: 0.7, 
                  marginBottom: '4px',
                  color: 'white'
                }}>
                  Played: {opponent.playedCards.length}/{opponent.spaces}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', gap: layout.isMobile ? '3px' : '6px' }}>
                  {opponent.playedCards.length === 0 ? (
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: fontSizes.label, padding: '10px' }}>
                      No cards
                    </div>
                  ) : (
                    opponent.playedCards.map((card, index) => renderCard(card, index, false, true))
                  )}
              </div>
            </div>

              {/* Opponent Discard Pile - Top Card Visible */}
              <div style={{
                minWidth: layout.isMobile ? '75px' : layout.isTablet ? '100px' : '120px',
                maxWidth: layout.isMobile ? '85px' : layout.isTablet ? '110px' : '140px',
                backgroundColor: 'rgba(255, 165, 0, 0.15)',
                borderRadius: layout.isMobile ? '6px' : '10px',
                padding: layout.isMobile ? '5px' : '8px',
                border: isMyTurn && opponent.discardPile.length > 0 ? '2px solid #ffa500' : '1px solid rgba(255, 165, 0, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
                <div style={{ 
                  fontSize: layout.isMobile ? '9px' : '11px', 
                  color: '#ffa500',
                  marginBottom: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  Discard ({opponent.discardPile.length})
            </div>
                {opponent.discardPile.length > 0 ? (() => {
                  const topCard = opponent.discardPile[opponent.discardPile.length - 1];
                  const centerCards = gameState.getCenterCards();
                  const isShareable = canCardBeShared(topCard);
                  const meetsPrereqs = checkPrerequisites(topCard, localPlayer?.playedCards || [], centerCards);
                  const canPlayTopCard = isShareable && meetsPrereqs;
                  
                  // Determine the lock reason
                  const getLockReason = () => {
                    if (!isShareable) return 'WISDOM';
                    if (!meetsPrereqs) return 'LOCKED';
                    return '';
                  };
                  
                  return (
                    <div 
                      onClick={() => {
                        if (isMyTurn && localPlayer.usedSpaces < localPlayer.spaces) {
                          handlePlayFromOpponentDiscard(opponent.id, opponent.discardPile.length - 1);
                        }
                      }}
                      style={{
                        cursor: isMyTurn && canPlayTopCard ? 'pointer' : 'default',
                        transition: 'transform 0.2s ease',
                        position: 'relative',
                        opacity: isMyTurn && !canPlayTopCard ? 0.6 : 1,
                      }}
                    >
                      {renderCard(topCard, 'opp-discard-top', false, true, true)}
                      {isMyTurn && (
                        <div style={{
                          position: 'absolute',
                          bottom: layout.isMobile ? '2px' : '5px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: canPlayTopCard ? 'rgba(0, 200, 0, 0.9)' : 'rgba(200, 0, 0, 0.9)',
                          color: 'white',
                          padding: layout.isMobile ? '2px 4px' : '3px 6px',
                          borderRadius: '8px',
                          fontSize: layout.isMobile ? '7px' : '9px',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                        }}>
                          {canPlayTopCard ? 'PLAY' : `LOCKED: ${getLockReason()}`}
          </div>
        )}
                    </div>
                  );
                })() : (
        <div style={{
                    color: 'rgba(255,255,255,0.3)', 
                    fontSize: fontSizes.label,
          textAlign: 'center',
                    padding: '10px 3px'
                  }}>
                    Empty
                  </div>
                )}
                {opponent.discardPile.length > 1 && isMyTurn && !layout.isMobile && (
              <Button
                variant="outline-warning"
                size="sm"
                onClick={() => openOpponentDiscardModal(opponent)}
                    style={{ marginTop: '6px', fontSize: '9px', padding: '2px 6px' }}
              >
                    View All
              </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Turn Indicator - Compact */}
        <div style={{
          textAlign: 'center',
          margin: `${spacing.gap} 0`,
          padding: layout.isMobile ? '8px' : '10px',
          background: localPlayer?.isFinished 
            ? 'rgba(155, 89, 182, 0.2)' 
            : (isMyTurn ? 'rgba(0, 255, 0, 0.2)' : 'rgba(255, 165, 0, 0.2)'),
          borderRadius: layout.isMobile ? '6px' : '10px',
          border: `2px solid ${localPlayer?.isFinished ? '#9b59b6' : (isMyTurn ? '#00ff00' : '#ffa500')}`,
          flexShrink: 0,
        }}>
          <strong className="appText" style={{ 
            fontSize: fontSizes.header, 
            color: localPlayer?.isFinished ? '#9b59b6' : (isMyTurn ? '#00ff00' : '#ffa500') 
          }}>
            {localPlayer?.isFinished 
              ? `FINISHED - Waiting for ${opponent?.name}...`
              : opponent?.isFinished
                ? `${opponent?.name} finished! Your turn to catch up!`
                : (isMyTurn ? 'YOUR TURN' : `${currentPlayer?.name}'s Turn`)}
          </strong>
          {!localPlayer?.isFinished && isMyTurn && !layout.isMobile && (
            <div style={{ fontSize: fontSizes.label, color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
              {opponent?.isFinished 
                ? `Beat ${opponent?.energy || 0} energy to win!`
                : 'Play or discard a card'}
            </div>
          )}
          {localPlayer?.isFinished && (
            <div style={{ fontSize: fontSizes.label, color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
              Your final energy: {localPlayer.energy}
            </div>
          )}
        </div>

        {/* Center Cards Area - Shared cards from opponent's discard */}
        {gameState.centerCards && gameState.centerCards.length > 0 && (
          <div style={{
            marginBottom: spacing.section,
            flexShrink: 0,
          }}>
            <div style={{
              backgroundColor: 'rgba(155, 89, 182, 0.15)',
              borderRadius: layout.isMobile ? '6px' : '10px',
              padding: layout.isMobile ? '6px' : '10px',
              border: '2px solid rgba(155, 89, 182, 0.4)',
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '6px',
              }}>
                <div style={{ 
                  fontSize: fontSizes.label, 
                  color: '#9b59b6',
                  fontWeight: 'bold',
                }}>
                  🔄 SHARED CARDS ({gameState.centerCards.length})
                </div>
                <div style={{ 
                  fontSize: layout.isMobile ? '8px' : '10px', 
                  color: 'rgba(255,255,255,0.6)',
                }}>
                  Count as prerequisites for both players
                </div>
              </div>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                justifyContent: 'center', 
                gap: layout.isMobile ? '4px' : '8px',
                maxHeight: layout.isMobile ? '90px' : '120px',
                overflowY: 'auto',
              }}>
                {gameState.centerCards.map((card, index) => 
                  renderCard(card, `center-${index}`, false, false, false, true)
                )}
              </div>
            </div>
          </div>
        )}

        {/* Your Board (Bottom) - Flex grow to fill remaining space */}
        <div style={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}>
          {/* Your Stats Header - Pill Style like Solitaire */}
          <div className="header" style={{ 
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: layout.isMobile ? '8px' : '15px',
            marginBottom: spacing.gap,
            marginTop: 0,
            flexWrap: 'nowrap',
            flexShrink: 0,
          }}>
            {/* Energy Pill */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)',
              border: '2px solid rgba(255, 193, 7, 0.3)',
              borderRadius: '50px',
              padding: layout.isMobile ? '6px 12px' : '10px 20px',
              display: 'flex',
              alignItems: 'center',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 20px rgba(255, 193, 7, 0.2), inset 0 0 20px rgba(255, 193, 7, 0.1)',
            }}>
              <svg 
                width={layout.isMobile ? 16 : 20} 
                height={layout.isMobile ? 16 : 20} 
                viewBox="0 0 24 24" 
                fill="none"
                style={{ 
                  marginRight: layout.isMobile ? '6px' : '10px',
                  filter: 'drop-shadow(0 0 4px rgba(255, 193, 7, 0.5))',
                }}
              >
                <path 
                  d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" 
                  fill="rgba(255, 193, 7, 0.8)"
                  stroke="rgba(255, 193, 7, 1)"
                  strokeWidth="1"
                />
              </svg>
              <span style={{ 
                fontSize: layout.isMobile ? '16px' : '20px', 
                fontWeight: 'bold',
                color: '#ffc107',
                textShadow: '0 0 10px rgba(255, 193, 7, 0.5)',
              }}>
                {localPlayer?.energy || 0}
              </span>
            </div>
            
            {/* Spaces/Cards Played Pill */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(0, 255, 127, 0.1) 0%, rgba(0, 200, 83, 0.1) 100%)',
              border: '2px solid rgba(0, 255, 127, 0.3)',
              borderRadius: '50px',
              padding: layout.isMobile ? '6px 12px' : '10px 20px',
              display: 'flex',
              alignItems: 'center',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 20px rgba(0, 255, 127, 0.2), inset 0 0 20px rgba(0, 255, 127, 0.1)',
            }}>
              <svg 
                width={layout.isMobile ? 16 : 20} 
                height={layout.isMobile ? 16 : 20} 
                viewBox="0 0 24 24" 
                fill="none"
                style={{ 
                  marginRight: layout.isMobile ? '6px' : '10px',
                  filter: 'drop-shadow(0 0 4px rgba(0, 255, 127, 0.5))',
                }}
              >
                <rect x="5" y="4" width="10" height="14" rx="1" fill="rgba(0, 255, 127, 0.6)" stroke="rgba(0, 255, 127, 0.8)" strokeWidth="1"/>
                <rect x="8" y="2" width="10" height="14" rx="1" fill="rgba(0, 255, 127, 0.8)" stroke="rgba(0, 255, 127, 1)" strokeWidth="1"/>
              </svg>
              <span style={{ 
                fontSize: layout.isMobile ? '14px' : '18px', 
                fontWeight: 'bold',
                color: '#00ff7f',
                textShadow: '0 0 10px rgba(0, 255, 127, 0.5)',
              }}>
                {localPlayer?.usedSpaces || 0}/{localPlayer?.spaces || 10}
              </span>
            </div>
            
            {/* Deck Pill */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(135, 206, 235, 0.1) 0%, rgba(100, 149, 237, 0.1) 100%)',
              border: '2px solid rgba(135, 206, 235, 0.3)',
              borderRadius: '50px',
              padding: layout.isMobile ? '6px 12px' : '10px 20px',
              display: 'flex',
              alignItems: 'center',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 20px rgba(135, 206, 235, 0.2), inset 0 0 20px rgba(135, 206, 235, 0.1)',
            }}>
              <svg 
                width={layout.isMobile ? 16 : 20} 
                height={layout.isMobile ? 16 : 20} 
                viewBox="0 0 24 24" 
                fill="none"
                style={{ 
                  marginRight: layout.isMobile ? '6px' : '10px',
                  filter: 'drop-shadow(0 0 4px rgba(135, 206, 235, 0.5))',
                }}
              >
                <rect x="3" y="6" width="12" height="16" rx="1" fill="rgba(135, 206, 235, 0.3)" stroke="rgba(135, 206, 235, 0.5)" strokeWidth="1" transform="rotate(-5 9 14)"/>
                <rect x="5" y="5" width="12" height="16" rx="1" fill="rgba(135, 206, 235, 0.5)" stroke="rgba(135, 206, 235, 0.7)" strokeWidth="1" transform="rotate(-2 11 13)"/>
                <rect x="7" y="4" width="12" height="16" rx="1" fill="rgba(135, 206, 235, 0.8)" stroke="rgba(135, 206, 235, 1)" strokeWidth="1"/>
              </svg>
              <span style={{ 
                fontSize: layout.isMobile ? '14px' : '18px', 
                fontWeight: 'bold',
                color: '#87ceeb',
                textShadow: '0 0 10px rgba(135, 206, 235, 0.5)',
              }}>
                {localPlayer?.deck.length || 0}
              </span>
            </div>
          </div>

          {/* Your board row: Played Cards + Your Discard Pile */}
          <div style={{ 
            display: 'flex', 
            gap: spacing.gap, 
            marginBottom: spacing.gap,
            flex: layout.isMobile ? '0 0 auto' : '1 1 auto',
            minHeight: layout.isMobile ? '90px' : '120px',
            maxHeight: layout.isMobile ? '110px' : layout.isTablet ? '150px' : '200px',
          }}>
          {/* Your Played Cards Area */}
          <div 
            ref={playAreaRef}
            className="played-cards-area" 
            style={{
                flex: 1,
                backgroundColor: isDraggingToPlay ? 'rgba(0, 255, 0, 0.35)' : 'rgba(0, 255, 0, 0.125)',
                boxShadow: isDraggingToPlay ? '0px 0px 20px rgba(0, 255, 0, 0.4)' : '0px 0px 8px rgba(0, 0, 0, 0.10)',
                borderRadius: layout.isMobile ? '6px' : '10px',
              display: 'flex',
              flexDirection: 'column',
                position: 'relative',
                padding: layout.isMobile ? '5px' : '8px',
                transition: 'all 0.2s ease',
                overflow: 'auto',
                border: isDraggingToPlay ? '2px solid rgba(0, 255, 0, 0.6)' : '1px solid transparent',
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDraggingToPlay(true);
            }}
            onDragLeave={() => setIsDraggingToPlay(false)}
            onDrop={(e) => {
              e.preventDefault();
              handleDropOnPlayArea();
            }}
          >
              <div style={{ 
                fontSize: fontSizes.label, 
                opacity: 0.8, 
                marginBottom: '4px',
                color: 'white',
                width: '100%'
              }}>
                Played: {localPlayer?.usedSpaces || 0}/{localPlayer?.spaces || 10}
              </div>
            {isDraggingToPlay && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 255, 0, 0.3)',
                  border: '2px dashed #00ff00',
                  borderRadius: layout.isMobile ? '6px' : '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                  pointerEvents: 'none',
                  zIndex: 10,
              }}>
                  <strong style={{ color: '#00ff00', fontSize: fontSizes.header }}>DROP TO PLAY</strong>
              </div>
            )}
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', width: '100%', gap: layout.isMobile ? '3px' : '6px' }}>
                {localPlayer?.playedCards.length === 0 ? (
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: fontSizes.label, padding: '15px' }}>
                    No cards played
                  </div>
                ) : (
                  localPlayer?.playedCards.map((card, index) => renderCard(card, index, false))
                )}
            </div>
          </div>

            {/* Your Discard Pile - Top Card Visible */}
          <div 
            ref={discardAreaRef}
            style={{
                minWidth: layout.isMobile ? '75px' : layout.isTablet ? '100px' : '120px',
                maxWidth: layout.isMobile ? '85px' : layout.isTablet ? '110px' : '140px',
                backgroundColor: isDraggingToDiscard ? 'rgba(255, 100, 100, 0.4)' : 'rgba(128, 128, 128, 0.15)',
                borderRadius: layout.isMobile ? '6px' : '10px',
                padding: layout.isMobile ? '5px' : '8px',
                border: isDraggingToDiscard ? '2px solid #ff6666' : '1px solid rgba(128, 128, 128, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
                position: 'relative',
                transition: 'all 0.2s ease',
                boxShadow: isDraggingToDiscard ? '0px 0px 20px rgba(255, 100, 100, 0.4)' : 'none',
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDraggingToDiscard(true);
            }}
            onDragLeave={() => setIsDraggingToDiscard(false)}
            onDrop={(e) => {
              e.preventDefault();
              handleDropOnDiscardArea();
            }}
          >
              <div style={{ 
                fontSize: layout.isMobile ? '9px' : '11px', 
                color: '#aaa',
                marginBottom: '4px',
                textAlign: 'center',
                fontWeight: 'bold'
              }}>
                Discard ({localPlayer?.discardPile.length || 0})
              </div>
            {isDraggingToDiscard && (
              <div style={{
                position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: '#ff6666',
                  fontSize: fontSizes.subHeader,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  zIndex: 10,
                }}>
                  DROP
              </div>
            )}
              {localPlayer?.discardPile.length > 0 ? (
                <div style={{ opacity: isDraggingToDiscard ? 0.3 : 0.8 }}>
                  {renderCard(localPlayer.discardPile[localPlayer.discardPile.length - 1], 'my-discard-top', false, false, true)}
                </div>
              ) : (
                <div style={{ 
                  color: 'rgba(255,255,255,0.3)', 
                  fontSize: fontSizes.label,
                  textAlign: 'center',
                  padding: '15px 3px'
                }}>
                  Empty
                </div>
              )}
            </div>
          </div>

          {/* Your Hand Area - Fanned like Solitaire */}
          <div 
            className="hand-area" 
            style={{
              position: 'fixed',
              bottom: '20px',
              left: '0',
              right: '0',
              height: '240px',
              backgroundColor: 'transparent',
              zIndex: 100,
              padding: '0',
              overflow: 'visible',
              touchAction: 'none',
            }}
            onDragOver={(e) => {
              e.preventDefault();
              handleDragOver(e);
            }}
            onDragLeave={() => {
              setIsDraggingToPlay(false);
              setIsDraggingToDiscard(false);
            }}
          >
            {/* Fanned cards container - matching Solitaire layout */}
            <div style={{ 
              position: 'relative',
              width: '100%',
              height: '200px',
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '0',
              overflow: 'visible'
            }}>
              <div style={{
                position: 'relative',
                width: `${Math.max(((localPlayer?.hand.length || 1) - 1) * cardSpacing + 140, 200)}px`,
                height: '100%',
                overflow: 'visible',
              }}>
                {localPlayer?.hand.map((card, index) => renderCard(card, index, true, false, false, false, true, localPlayer.hand.length))}
              </div>
            </div>
            
            {/* Turn indicator below hand */}
            {isMyTurn && (
              <div style={{
                position: 'absolute',
                bottom: '-5px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 255, 0, 0.2)',
                padding: '4px 20px',
                borderRadius: '20px',
                border: '1px solid rgba(0, 255, 0, 0.4)',
                fontSize: '12px',
                color: '#00ff00',
                fontWeight: 'bold',
              }}>
                Your Turn - Drag to Play
              </div>
            )}
          </div>
        </div>

        {/* Floating Card Visual for Drag - matching Solitaire */}
        {touchDragInitiated && draggedCard && touchDragPosition && (
          <div
            style={{
              position: 'fixed',
              left: touchDragPosition.x - 70,
              top: touchDragPosition.y - 100,
              width: '140px',
              height: '200px',
              zIndex: 10001,
              pointerEvents: 'none',
              transform: 'rotate(-5deg) scale(1.1)',
              opacity: 0.95,
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5), 0 0 60px rgba(255, 255, 255, 0.4)',
              borderRadius: '10px',
              overflow: 'hidden',
              background: 'white',
            }}
          >
            <img 
              src={draggedCard.imageUrl} 
              alt={draggedCard.name}
              style={{
                width: '100%',
                height: '65%',
                objectFit: 'cover',
                borderRadius: '10px 10px 0 0',
              }}
            />
            <div style={{
              height: '35%',
              padding: '5px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}>
              <div style={{ 
                fontSize: '12px', 
                fontWeight: 'bold',
                color: '#000',
              }}>
                {draggedCard.name}
              </div>
              <div style={{ 
                fontSize: '10px', 
                color: '#666',
                lineHeight: '1.2',
              }}>
                {draggedCard.description}
              </div>
            </div>
          </div>
        )}

        {/* Expanded Card View Modal (same as Solitaire) */}
        {(expandedCard || isClosingExpanded) && expandedCard && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              pointerEvents: 'auto',
            }}
          >
            {/* Backdrop */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                backdropFilter: 'blur(5px)',
                transition: 'opacity 0.3s ease',
                opacity: expandedCard && !isClosingExpanded ? 1 : 0,
                pointerEvents: 'auto',
                cursor: 'pointer',
              }}
              onClick={closeExpandedCard}
            />
            
            {/* Animated Card - Same as Solitaire with position animation */}
            <div
              style={{
                position: 'absolute',
                left: expandedCardPosition ? expandedCardPosition.left : '50%',
                top: expandedCardPosition ? expandedCardPosition.top : '50%',
                width: expandedCardPosition ? expandedCardPosition.width : 320,
                height: expandedCardPosition ? expandedCardPosition.height : Math.min(window.innerHeight * 0.85, 550),
                transform: expandedCardPosition ? 'none' : 'translate(-50%, -50%)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                ...(!expandedCardPosition && {
                  left: '50%',
                  top: '50%',
                  width: 320,
                  height: Math.min(window.innerHeight * 0.85, 550),
                  transform: 'translate(-50%, -50%) scale(1)',
                }),
                pointerEvents: 'auto',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  padding: '10px',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Card Image */}
                <img 
                  src={expandedCard.imageUrl} 
                  alt={expandedCard.name}
                  style={{
                    width: '100%',
                    height: '50%',
                    minHeight: '180px',
                    maxHeight: '280px',
                    objectFit: 'cover',
                    borderRadius: '10px',
                    marginBottom: '10px',
                    flexShrink: 0,
                  }}
                />
                
                {/* Card Details */}
                <div style={{
                  padding: '10px',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  overflowY: 'auto',
                  minHeight: 0,
                }}>
                  <div style={{ textAlign: 'center', flexShrink: 0 }}>
                    <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', marginBottom: '10px', color: '#000' }}>
                      {expandedCard.name}
                    </h2>
                    <p style={{ margin: 0, fontSize: '14px', color: '#333', lineHeight: '1.5', maxHeight: '120px', overflowY: 'auto' }}>
                      {expandedCard.description}
                    </p>
                  </div>
                  
                  <div style={{ flex: 1, minHeight: '10px' }} />
                  
                  {/* Prerequisites badge */}
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
                    {expandedCard.prerequisites && expandedCard.prerequisites.length > 0 && (
                      <div style={{
                        backgroundColor: '#fff3e0',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '14px',
                        color: '#f57c00',
                      }}>
                        Requires: {expandedCard.prerequisites.join(', ')}
                      </div>
                    )}
                  </div>
                  
                  {/* Action Buttons (only for hand cards on your turn) */}
                  {expandedCardIsInHand && isMyTurn && (
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      marginTop: '12px',
                      flexShrink: 0,
                    }}>
                      <button
                        style={{
                          flex: 1,
                          padding: '16px 20px',
                          minHeight: '48px',
                          backgroundColor: checkPrerequisites(expandedCard, localPlayer?.playedCards || [], gameState?.getCenterCards() || []) ? '#4caf50' : '#ccc',
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          cursor: checkPrerequisites(expandedCard, localPlayer?.playedCards || [], gameState?.getCenterCards() || []) ? 'pointer' : 'not-allowed',
                          transition: 'all 0.3s ease',
                        }}
                        onClick={() => {
                          if (checkPrerequisites(expandedCard, localPlayer?.playedCards || [], gameState?.getCenterCards() || [])) {
                            handlePlayFromHand(expandedCardIndex);
                            closeExpandedCard();
                          }
                        }}
                        disabled={!checkPrerequisites(expandedCard, localPlayer?.playedCards || [], gameState?.getCenterCards() || [])}
                      >
                        Play Card
                      </button>
                      <button
                        style={{
                          padding: '16px 30px',
                          minHeight: '48px',
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                        }}
                        onClick={() => {
                          handleDiscard(expandedCardIndex);
                          closeExpandedCard();
                        }}
                      >
                        Discard
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AI Thinking Indicator - Responsive */}
        {aiThinking && (
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(0,0,0,0.9)',
            padding: layout.isMobile ? '20px' : '30px',
            borderRadius: layout.isMobile ? '10px' : '15px',
            border: '2px solid #ffd700',
            zIndex: 1000
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: layout.isMobile ? '24px' : '32px', 
                marginBottom: '10px',
                color: '#ffd700',
                fontWeight: 'bold',
              }}>AI</div>
              <div style={{ color: '#ffd700', fontSize: layout.isMobile ? '14px' : '18px' }}>AI thinking...</div>
            </div>
          </div>
        )}

        {/* Opponent Discard Modal - Responsive */}
        <Modal
          show={showOpponentDiscardModal}
          onHide={() => {
            setShowOpponentDiscardModal(false);
            setSelectedCardFromOpponent(null);
            setSelectedOpponentDiscard(null);
          }}
          size={layout.isMobile ? 'sm' : 'lg'}
          centered
        >
          <Modal.Header closeButton style={{ background: '#222', color: 'white', padding: layout.isMobile ? '10px 15px' : '15px 20px' }}>
            <Modal.Title style={{ fontSize: layout.isMobile ? '16px' : '20px' }}>
              {opponent?.name}'s Discard
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ 
            background: '#333', 
            color: 'white', 
            maxHeight: layout.isMobile ? '50vh' : '60vh', 
            overflowY: 'auto',
            padding: layout.isMobile ? '10px' : '20px',
          }}>
            <p style={{ marginBottom: '10px', color: '#ffd700', fontSize: layout.isMobile ? '12px' : '14px' }}>
              Only the top card can be played (Wisdom cards except Love cannot be shared)
            </p>
            <div style={{
              display: 'flex',
              gap: layout.isMobile ? '6px' : '10px',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              {opponent?.discardPile.map((card, index) => {
                const centerCards = gameState.getCenterCards();
                const isTopCard = index === opponent.discardPile.length - 1;
                const isShareable = canCardBeShared(card);
                const meetsPrereqs = checkPrerequisites(card, localPlayer?.playedCards || [], centerCards);
                // Can only play if it's the top card AND meets other requirements
                const canPlay = isTopCard && isShareable && meetsPrereqs;
                
                // Determine lock reason for display
                const getLockReason = () => {
                  if (!isTopCard) return 'NOT TOP';
                  if (!isShareable) return 'WISDOM';
                  if (!meetsPrereqs) return 'PREREQS';
                  return '';
                };
                
                return (
                  <div
                    key={card.id || index}
                    onClick={() => {
                      // Only allow selecting the top card
                      if (isTopCard && canPlay) {
                        setSelectedCardFromOpponent(index);
                      }
                    }}
                  style={{
                      cursor: isTopCard && canPlay ? 'pointer' : 'not-allowed',
                      border: selectedCardFromOpponent === index ? '3px solid #ffd700' : 
                             isTopCard && canPlay ? '2px solid #27ae60' : 
                             isTopCard ? '2px solid #e74c3c' : '2px solid #555',
                      borderRadius: layout.isMobile ? '8px' : '12px',
                      transform: selectedCardFromOpponent === index ? 'scale(1.03)' : 'scale(1)',
                      transition: 'all 0.2s',
                      position: 'relative',
                      opacity: isTopCard ? (canPlay ? 1 : 0.6) : 0.35,
                    }}
                  >
                    {renderCard(card, index, false, false, true)}
                    {isTopCard && (
                      <div style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        backgroundColor: canPlay ? '#27ae60' : '#ffa500',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '8px',
                        fontSize: layout.isMobile ? '8px' : '10px',
                        fontWeight: 'bold'
                      }}>
                        TOP CARD
                </div>
                    )}
                    {isTopCard && !canPlay && (
                      <div style={{
                        position: 'absolute',
                        bottom: '5px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'rgba(231, 76, 60, 0.9)',
                        color: 'white',
                        padding: '2px 5px',
                        borderRadius: '8px',
                        fontSize: layout.isMobile ? '7px' : '9px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                      }}>
                        {getLockReason()}
                      </div>
                    )}
                    {!isTopCard && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        borderRadius: layout.isMobile ? '8px' : '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <span style={{ 
                          color: 'rgba(255,255,255,0.7)', 
                          fontSize: layout.isMobile ? '8px' : '10px',
                          fontWeight: 'bold' 
                        }}>
                          BURIED
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Modal.Body>
          <Modal.Footer style={{ background: '#222', padding: layout.isMobile ? '8px 12px' : '12px 20px' }}>
            <Button 
              variant="secondary" 
              size={layout.isMobile ? 'sm' : 'md'}
              onClick={() => {
                setShowOpponentDiscardModal(false);
                setSelectedCardFromOpponent(null);
                setSelectedOpponentDiscard(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="warning"
              size={layout.isMobile ? 'sm' : 'md'}
              onClick={() => handlePlayFromOpponentDiscard(opponent?.id, opponent?.discardPile.length - 1)}
              disabled={!opponent?.discardPile.length || 
                (() => {
                  const topCard = opponent?.discardPile[opponent.discardPile.length - 1];
                  return !topCard || !canCardBeShared(topCard) || 
                    !checkPrerequisites(topCard, localPlayer?.playedCards || [], gameState.getCenterCards());
                })()}
            >
              Play Top Card
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Game End Modal - Responsive */}
        <Modal
          show={showGameEndModal}
          onHide={() => {}}
          centered
          backdrop="static"
          size={layout.isMobile ? 'sm' : 'md'}
        >
          <Modal.Header style={{ 
            background: '#222', 
            color: 'white', 
            borderBottom: '2px solid #ffd700',
            padding: layout.isMobile ? '10px 15px' : '15px 20px'
          }}>
            <Modal.Title style={{ fontSize: layout.isMobile ? '18px' : '22px' }}>Game Over!</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ 
            background: '#333', 
            color: 'white', 
            textAlign: 'center', 
            padding: layout.isMobile ? '15px' : '25px' 
          }}>
            {gameState.winner && (
              <>
                <div style={{ fontSize: layout.isMobile ? '36px' : '48px', marginBottom: '15px' }}>
                  {gameState.winner.id === localPlayerId ? 'VICTORY' : 'DEFEAT'}
                </div>
                <h3 style={{ color: '#ffd700', marginBottom: '15px', fontSize: layout.isMobile ? '18px' : '24px' }}>
                  {gameState.winner.id === localPlayerId ? 'You Won!' : `${gameState.winner.name} Wins!`}
                </h3>
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ marginBottom: '10px', fontSize: layout.isMobile ? '14px' : '18px' }}>Final Scores:</h4>
                  {gameState.players.map(player => (
                    <div
                      key={player.id}
                      style={{
                        padding: layout.isMobile ? '8px' : '10px',
                        marginBottom: '8px',
                        background: player.id === gameState.winner.id ? 'rgba(255, 215, 0, 0.2)' : 'rgba(255,255,255,0.05)',
                        borderRadius: '5px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: layout.isMobile ? '13px' : '16px'
                      }}
                    >
                      <span>{player.name}</span>
                      <span style={{ fontWeight: 'bold', color: '#ffd700' }}>
                        Energy: {player.energy}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer style={{ background: '#222', justifyContent: 'center', padding: layout.isMobile ? '10px' : '15px' }}>
            <Button variant="warning" size={layout.isMobile ? 'md' : 'lg'} onClick={onExit}>
              Back to Menu
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Exit Button - Responsive */}
        <div style={{ 
          position: 'fixed', 
          top: layout.isMobile ? '8px' : '15px', 
          right: layout.isMobile ? '8px' : '15px', 
          zIndex: 100 
        }}>
          <Button
            variant="danger"
            size={layout.isMobile ? 'sm' : 'md'}
            style={{
              fontSize: layout.isMobile ? '12px' : '14px',
              padding: layout.isMobile ? '4px 10px' : '6px 14px',
            }}
            onClick={() => {
              if (window.confirm('Are you sure you want to exit?')) {
                onExit();
              }
            }}
          >
            Exit
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MultiplayerGame;
